// Import a project from a draftlize export zip.
//
// UX is intentionally bare-bones for v1: pick a file (or drop one), tweak
// the project name if you like, click Import. Validation is server-side —
// the zip is small (KB to single-digit MB), the upload is fast, and the
// server returns a precise error string we surface verbatim. Adding a
// client-side zip parser just to preview manifest counts isn't worth a
// new dependency yet.

import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Project } from '../api';
import { api } from '../api';

export interface ImportModalProps {
  /** Called with the new project after a successful import. The parent is
   *  responsible for refreshing the project list and switching to it. */
  onImported: (project: Project) => void;
  onClose: () => void;
}

type Stage =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'uploading'; file: File }
  | { kind: 'error'; file: File | null; message: string };

export function ImportModal({ onImported, onClose }: ImportModalProps) {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [name, setName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const file =
    stage.kind === 'ready' || stage.kind === 'uploading' || stage.kind === 'error'
      ? stage.file ?? null
      : null;
  const uploading = stage.kind === 'uploading';

  const acceptFile = (f: File | null | undefined) => {
    if (!f) return;
    // Accept anything ending in .zip OR with the application/zip MIME — some
    // browsers/OSes lie about either, so checking both is the only way to
    // not falsely reject a real export. Server-side validation catches the
    // truly invalid case.
    const looksLikeZip =
      f.name.toLowerCase().endsWith('.zip') ||
      f.type === 'application/zip' ||
      f.type === 'application/x-zip-compressed';
    if (!looksLikeZip) {
      setStage({
        kind: 'error',
        file: null,
        message: t('import.errNotZip', { name: f.name }),
      });
      return;
    }
    setStage({ kind: 'ready', file: f });
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0]);
    // Reset the input so picking the same file twice in a row still fires.
    e.target.value = '';
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (!file || uploading) return;
    setStage({ kind: 'uploading', file });
    try {
      const project = await api.importProject(file, name);
      onImported(project);
    } catch (err) {
      setStage({
        kind: 'error',
        file,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26, 24, 20, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1500,
        padding: 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !uploading) onClose();
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 8,
          width: 'min(560px, 92vw)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 22px',
            borderBottom: '1px solid var(--rule)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span className="serif" style={{ fontSize: 18, fontWeight: 500 }}>
            {t('import.heading')}
          </span>
          <span style={{ flex: 1 }} />
          <button
            onClick={onClose}
            disabled={uploading}
            className="mono"
            style={{
              fontSize: 12,
              color: 'var(--ink-3)',
              padding: '4px 10px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: 'transparent',
              cursor: uploading ? 'not-allowed' : 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '18px 22px 4px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {/* Drop zone / file slot */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => {
              if (!uploading) inputRef.current?.click();
            }}
            style={{
              padding: '22px 16px',
              border: `1.5px dashed ${dragOver ? 'var(--blue)' : 'var(--rule)'}`,
              borderRadius: 6,
              background: dragOver ? 'rgba(56, 132, 222, 0.06)' : 'var(--paper-1)',
              textAlign: 'center',
              cursor: uploading ? 'progress' : 'pointer',
              transition: 'border-color 80ms, background 80ms',
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".zip,application/zip"
              onChange={onPick}
              style={{ display: 'none' }}
            />
            {file ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 13, color: 'var(--ink-1)', fontWeight: 500 }}>
                  📦 {file.name}
                </span>
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                  {formatBytes(file.size)}
                  {!uploading && ` · ${t('import.changeFile')}`}
                </span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  {t('import.dropZone')}
                </span>
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                  {t('import.dropZoneHint')}
                </span>
              </div>
            )}
          </div>

          {/* Name override */}
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--ink-3)',
                letterSpacing: '.1em',
                fontWeight: 600,
                display: 'block',
                marginBottom: 4,
              }}
            >
              {t('import.projectNameLabel')}
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('import.projectNamePlaceholder')}
              disabled={uploading}
              style={{
                width: '100%',
                fontSize: 12.5,
                padding: '6px 8px',
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background: uploading ? 'var(--paper-1)' : 'white',
                color: 'var(--ink-1)',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Error band — keeps the rest of the modal visible so the user
              can swap files / change the name and try again without
              starting over. */}
          {stage.kind === 'error' && (
            <div
              style={{
                padding: '8px 10px',
                fontSize: 12,
                background: 'rgba(210, 63, 63, 0.08)',
                color: 'var(--red, #d23f3f)',
                borderRadius: 4,
                border: '1px solid rgba(210, 63, 63, 0.2)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {stage.message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '14px 22px',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            borderTop: '1px solid var(--rule)',
            background: 'var(--paper-1)',
            borderRadius: '0 0 8px 8px',
          }}
        >
          <button
            onClick={onClose}
            disabled={uploading}
            className="mono"
            style={{
              fontSize: 11,
              padding: '6px 12px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              color: 'var(--ink-2)',
              background: 'white',
              cursor: uploading ? 'not-allowed' : 'pointer',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={submit}
            disabled={!file || uploading}
            className="mono"
            style={{
              fontSize: 11,
              padding: '6px 14px',
              borderRadius: 4,
              border: '1px solid var(--ink-0)',
              background: !file || uploading ? 'var(--ink-4)' : 'var(--ink-0)',
              color: 'white',
              fontWeight: 600,
              letterSpacing: '.04em',
              cursor: !file || uploading ? 'not-allowed' : 'pointer',
            }}
          >
            {uploading ? t('import.importing') : t('import.importButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
